import React, { useCallback } from 'react'

import { classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { arrayOf, bool, number, string } from 'prop-types'

import I18n from 'Pods/Shared/I18n'

import formatPrice from 'Utils/formatPrice'

const TRANSLATION_KEY_PREFIX = 'subscription_checkout.order_summary'
const FEATURE_SLOTS = ['first', 'second', 'third']

function LegacyPlanCard({
  locale = 'en',
  planName,
  features,
  originalPrice = null,
  currentPrice,
  selectedPlanKey = null,
  subscriptionV16Enabled = false,
  subscriptionV17CatalogEnabled = false,
  isPremium = false,
  isSinglePlanCard = true,
  priceUnit = null,
  catalogValue = '',
}) {
  const translate = useCallback(
    (key, options = {}) => I18n.t(key, { ns: 'rails', lng: locale, ...options }),
    [locale],
  )

  const planKeyedTranslate = (suffix, options = {}) => {
    if (!selectedPlanKey) {
      return translate(`${TRANSLATION_KEY_PREFIX}.${suffix}`, options)
    }
    return translate(`${TRANSLATION_KEY_PREFIX}.plans.${selectedPlanKey}.${suffix}`, options)
  }

  // subscription_v1_7: the backend sends the tier-aware title/price/unit/bullets; render them
  // verbatim instead of the FE plan-keyed copy. Premium always takes this path (its checkout is
  // single-plan and tier-driven), even if the v1.6 multi-plan selector is later re-enabled for
  // essentials — `isPremium` (backend tier) keeps premium on the backend-copy path while the
  // selector stays scoped to essentials.
  const useV17Copy = subscriptionV17CatalogEnabled && (isPremium || isSinglePlanCard)

  const displayedPlanName = useV17Copy || !selectedPlanKey
    ? planName
    : planKeyedTranslate('name')

  const billingPeriodLabel = planKeyedTranslate('billing_period')
  const priceUnitLabel = useV17Copy
    ? priceUnit || planKeyedTranslate('price_unit')
    : planKeyedTranslate('price_unit')

  const planFeatures = useV17Copy || !selectedPlanKey
    ? features
    : FEATURE_SLOTS.map((slot) => (
      translate(`${TRANSLATION_KEY_PREFIX}.plans.${selectedPlanKey}.features.${slot}`, {
        modelCount: catalogValue,
      })
    ))

  return (
    <div className={classnames('order-summary__plan', subscriptionV16Enabled && 'order-summary__plan--outlined')}>
      <div className="order-summary__plan-content">
        <span className="cgt-body-base-semibold">{displayedPlanName}</span>
        <ul className="order-summary__features">
          {planFeatures.map((feature) => (
            <li key={feature} className="order-summary__feature">
              <span className="order-summary__feature-bullet" />
              <span className="order-summary__feature-text cgt-caption">{feature}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="order-summary__plan-pricing">
        {billingPeriodLabel && (
          <span className="order-summary__billing-period cgt-caption-semibold">
            {billingPeriodLabel}
          </span>
        )}
        <div className="order-summary__price-wrap">
          {originalPrice && (
            <span className="order-summary__original-price cgt-body-base-semibold">
              {formatPrice(originalPrice)}
            </span>
          )}
          <span className="order-summary__current-price cgt-body-lg-semibold">
            {formatPrice(currentPrice)}
          </span>
        </div>
        <span className="order-summary__price-unit cgt-caption-semibold">
          {priceUnitLabel}
        </span>
      </div>
    </div>
  )
}

LegacyPlanCard.propTypes = {
  locale: string,
  planName: string.isRequired,
  features: arrayOf(string).isRequired,
  originalPrice: number,
  currentPrice: number.isRequired,
  selectedPlanKey: string,
  subscriptionV16Enabled: bool,
  subscriptionV17CatalogEnabled: bool,
  isPremium: bool,
  isSinglePlanCard: bool,
  priceUnit: string,
  catalogValue: string,
}

export default LegacyPlanCard
